import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const CompletionRateChart = ({ tasks, familyMembers, period }) => {
  // Calculate completion rates per member
  const memberStats = useMemo(() => {
    return familyMembers?.map(member => {
      const memberTasks = tasks?.filter(task => task?.personId === member?.id);
      const completed = memberTasks?.filter(task => task?.completed)?.length;
      const total = memberTasks?.length;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        ...member,
        completed,
        total,
        percentage,
        pending: total - completed
      };
    });
  }, [tasks, familyMembers]);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="pie-chart" size={24} className="text-primary" />
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            Completion Rate Summary
          </h2>
          <p className="text-sm text-muted-foreground">Task completion by family member</p>
        </div>
      </div>

      <div className="space-y-5">
        {memberStats?.map((member) => (
          <div key={member?.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center text-white font-semibold text-sm md:text-base"
                  style={{ backgroundColor: member?.color }}
                >
                  {member?.initials}
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-medium text-foreground">
                    {member?.name}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground">{member?.role}</p>
                </div>
              </div>

              <div className="text-right">
                <div className="text-2xl md:text-3xl font-bold" style={{ color: member?.color }}>
                  {member?.percentage}%
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">
                  {member?.completed}/{member?.total} tasks
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="relative">
              <div className="w-full bg-muted rounded-full h-3 md:h-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${member?.percentage}%`,
                    backgroundColor: member?.color
                  }}
                />
              </div>
            </div>

            {/* Task Breakdown */}
            <div className="flex items-center justify-between text-xs md:text-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 text-success">
                  <Icon name="check-circle" size={14} />
                  <span>{member?.completed} completed</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Icon name="circle" size={14} />
                  <span>{member?.pending} pending</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Overall Summary */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm md:text-base font-medium text-foreground">Family Average</span>
            <span className="text-2xl md:text-3xl font-bold text-primary">
              {Math.round(
                memberStats?.reduce((sum, m) => sum + m?.percentage, 0) / memberStats?.length
              )}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionRateChart;